import { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from './use-toast';
import { useActiveTheme } from '../lib/activeTheme';
import { themeLaunchesDirectly } from '../lib/siteThemes';
import { MODE_DEMO, MODE_REAL, gameDemoAvailable } from '../lib/gamePage';
import { useGamePage } from './useGamePage';

const DEMO_ID_KEY = 'pusubet_demo_id';

/** Tarayıcıda saklanan demo kimliği (demo bakiyesi buna bağlı). */
export const getDemoId = () => {
  try { return localStorage.getItem(DEMO_ID_KEY) || null; } catch { return null; }
};
const setDemoId = (id) => {
  try { if (id) localStorage.setItem(DEMO_ID_KEY, id); } catch { /* yok sayılır */ }
};

/**
 * Oyun açma akışını yönetir:
 *   1. Oyuna tıklanınca seçim modalı açılır (choice)
 *   2. Gerçek para  -> /games/launch
 *      Demo         -> /games/demo-launch (giriş gerekmez)
 *
 * Demo yalnızca sağlayıcısı demo destekleyen oyunlarda sunulur.
 *
 * Şema `directLaunch` taşıyorsa (bugün yalnızca Betrun) seçim penceresi
 * **hiç** açılmaz: üye oyunu doğrudan gerçek modda, ziyaretçi demosu olan
 * oyunu doğrudan demoda açar; demosu olmayan oyunda ziyaretçiye giriş
 * penceresi çıkar. Pencerenin sorduğu soru (gerçek mi, demo mu) oyun
 * sayfasının Eğlence / Gerçek Oyun anahtarında zaten duruyor — aynı soruyu
 * oyun açılmadan önce ikinci kez sormak her oyunda fazladan bir dokunuştu.
 *
 * Şema `gamePage` taşıyorsa (bugün yalnızca Betrun) oyun pencerede değil
 * kendi sayfasında açılır (`/oyun/:id`, bkz. hooks/useGamePage.js):
 * `activeGame` hiç dolmaz, yani kabuklardaki `<GameModal>` o şemada çizilmez.
 */
export const useGameLauncher = () => {
  const { user, balance, bonusBalance } = useAuth();
  const { toast } = useToast();
  const [choice, setChoice] = useState(null);      // seçim modalındaki oyun
  const [activeGame, setActiveGame] = useState(null); // açılacak oyun
  const [mode, setMode] = useState('real');        // real | demo
  const [authTab, setAuthTab] = useState(null);    // login | register | null
  const directLaunch = themeLaunchesDirectly(useActiveTheme());
  const openGamePage = useGamePage();

  // Oyunu açar: sayfa kullanan şemada adrese gider, diğerlerinde pencereyi
  // doldurur.
  const open = useCallback((game, nextMode) => {
    if (openGamePage(game, nextMode)) return;
    setMode(nextMode);
    setActiveGame(game);
  }, [openGamePage]);

  // Demonun hangi API'den açılacağını sunucu çözer: oyunun kendi sağlayıcısı
  // demo veriyorsa ondan, vermiyorsa (Gold Palace) eşleştirilmiş kopyadan.
  // Bu yüzden burada sağlayıcı adı aranmaz — `demo_supported` sunucudan
  // efektif değer olarak gelir; sabit bir sağlayıcı koşulu bağlı demoları
  // görünmez yapardı.
  // Canlı masa demo vermez (sunucu da `demo_supported: false` döner); eski
  // önbellekten gelen bir kart yüzünden oyuncuya demo sunulmasın diye kategori
  // burada da okunur.
  const demoAvailable = useCallback((game) => gameDemoAvailable(game), []);

  const play = useCallback((game) => {
    if (!game) return;
    const integrated = !!game.api_provider && !!game.game_id;
    if (!integrated) {
      toast({
        title: 'Oyun kullanılamıyor',
        description: 'Bu oyun henüz sağlayıcı entegrasyonuna bağlı değil',
      });
      return;
    }
    if (directLaunch) {
      if (user) open(game, MODE_REAL);
      else if (demoAvailable(game)) open(game, MODE_DEMO);
      else setAuthTab('login');
      return;
    }
    // Giriş yapılmış olsun ya da olmasın önce seçim ekranı gösterilir.
    setChoice(game);
  }, [toast, directLaunch, demoAvailable, user, open]);

  const startReal = useCallback(() => {
    if (!choice) return;
    if (!user) { setAuthTab('login'); setChoice(null); return; }
    open(choice, MODE_REAL);
    setChoice(null);
  }, [choice, user, open]);

  const startDemo = useCallback(() => {
    if (!choice) return;
    if (!demoAvailable(choice)) {
      toast({ title: 'Demo yok', description: 'Bu oyun demo modunu desteklemiyor' });
      return;
    }
    open(choice, MODE_DEMO);
    setChoice(null);
  }, [choice, demoAvailable, toast, open]);

  const openAuth = useCallback((tab) => { setAuthTab(tab); setChoice(null); }, []);

  const closeGame = useCallback(() => { setActiveGame(null); setMode('real'); }, []);

  return {
    play,
    // seçim modalı
    choice, closeChoice: () => setChoice(null),
    demoAvailable: demoAvailable(choice),
    startReal, startDemo, openAuth,
    // oyun modalı
    activeGame, mode, closeGame,
    // giriş modalı
    authTab, setAuthTab,
    user, balance, bonusBalance,
    // demo kimliği yardımcıları
    getDemoId, setDemoId,
  };
};

export default useGameLauncher;
